/**
 * The POS receipt template contract as the editor sees it. Mirrors the API's
 * `src/util/posReceiptTemplate.js` (limits, keys, Compact defaults) — change both together.
 */

export const VERSION = 1;

export const FONT_WEIGHTS = [
  { value: "normal", label: "Regular" },
  { value: "500", label: "Medium" },
  { value: "600", label: "Semibold" },
  { value: "bold", label: "Bold" },
];
export const ALIGNS = [
  { value: "left", label: "Left" },
  { value: "center", label: "Centre" },
  { value: "right", label: "Right" },
];
export const PLACEMENTS = [
  { value: "newline", label: "On the next line" },
  { value: "inline", label: "Same line, in brackets" },
];

export const LIMITS = Object.freeze({
  fontSize: [8, 40],
  boldFontSize: [8, 48],
  logoWidth: [50, 400],
  logoHeight: [20, 300],
  imageWidth: [20, 400],
  imageHeight: [10, 600],
  rowGap: [0, 20],
  qrSize: [1, 16],
  textRows: 8,
  imageRows: 4,
  textLength: 120,
  name: 80,
});

export const LOCKED_KEYS = ["brandName", "invoiceTitle", "vatNumber", "zatcaQr"];
export const SEGMENT_KEYS = ["companyHeader", "invoiceDetail"];
/** Native Epson lines: always print after the footer image, in this order. Shown pinned. */
export const PINNED_FOOTER_KEYS = ["phone", "zatcaQr"];

export const ROW_LABELS = {
  logo: "Logo",
  brandName: "Company name",
  arabicName: "Company name (Arabic)",
  invoiceTitle: "Simplified Tax Invoice title",
  vatNumber: "VAT number",
  companyHeader: "Company header lines (address)",
  crNumber: "CR number",
  invoiceDetail: "Invoice footer lines",
  phone: "Phone number",
  zatcaQr: "ZATCA QR code",
  text: "Text line",
  image: "Image line",
};

export const ROW_HINTS = {
  companyHeader: "The receipt header lines set in the merchant system (split by |). A text-bold: line uses the bold size.",
  invoiceDetail: "The invoice detail lines set in the merchant system (split by |).",
  phone: "Printed as a native line after the footer, before the QR code.",
  zatcaQr: "Always printed last. Size is the Epson QR module size (3 = compact, 8 = large).",
};

export const SYSTEM_TEMPLATE_HINTS = {
  compact: "The built-in receipt, exactly as the POS prints it today.",
  large: "Bigger logo, bolder product lines, borders under each item and a large QR code.",
};

const textRow = (key, fontSize, fontWeight = "normal", extra = {}) => ({
  key, visible: true, fontSize, fontWeight, align: "center", ...extra,
});

export const COMPACT_LAYOUT = Object.freeze({
  version: VERSION,
  header: {
    rowGap: 0,
    rows: [
      { key: "logo", visible: true, width: 230, height: 90 },
      textRow("brandName", 20, "bold"),
      textRow("arabicName", 20, "bold"),
      textRow("invoiceTitle", 16, "normal"),
      textRow("vatNumber", 16, "600"),
      textRow("companyHeader", 16, "normal", { boldFontSize: 26 }),
      textRow("crNumber", 16, "normal"),
    ],
  },
  items: {
    fontSize: 16,
    fontWeight: "normal",
    secondaryFontSize: 16,
    rowGap: 5,
    rowDivider: false,
    arabicName: { show: true, placement: "newline", fontSize: 12 },
    modifiers: { show: true, arabic: { show: true, placement: "inline" } },
  },
  totals: { fontSize: 16, fontWeight: "normal", rowGap: 0, vatDivider: false },
  footer: {
    rows: [
      textRow("invoiceDetail", 16, "normal", { boldFontSize: 26 }),
      { key: "phone", visible: true },
      { key: "zatcaQr", visible: true, size: 3 },
    ],
  },
  epson: { feedBeforeCallNumber: false },
});

export const newTextRow = (section, rows) => {
  const used = new Set(rows.map((r) => r.id).filter(Boolean));
  let n = 1;
  while (used.has(`${section[0]}-${n}`)) n += 1;
  return { key: "text", id: `${section[0]}-${n}`, visible: true, en: "", ar: "", fontSize: 16, fontWeight: "normal", align: "center" };
};

export const newImageRow = (section, rows, url) => {
  const used = new Set(rows.map((r) => r.id).filter(Boolean));
  let n = 1;
  while (used.has(`${section[0]}-img-${n}`)) n += 1;
  return { key: "image", id: `${section[0]}-img-${n}`, visible: true, url, width: 200, height: 80, align: "center" };
};

export const clampNumber = (v, [min, max], fallback) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
};

export const countRows = (rows, key) => rows.filter((r) => r.key === key).length;

/** Problems an admin can fix before saving (the server repeats these checks). */
export const findLayoutProblems = (layout) => {
  const problems = [];
  for (const section of ["header", "footer"]) {
    const rows = layout?.[section]?.rows || [];
    rows.forEach((r, i) => {
      if (r.key === "text" && !String(r.en || "").trim() && !String(r.ar || "").trim()) {
        problems.push(`${section === "header" ? "Header" : "Footer"} text line ${i + 1} is empty`);
      }
      if (r.key === "image" && !r.url) {
        problems.push(`${section === "header" ? "Header" : "Footer"} image line ${i + 1} has no image`);
      }
    });
  }
  return problems;
};
